import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Model as SurveyModel } from 'survey-core';

import { formatDate } from '@core/utils/date.utils';
import { hasScoring } from '@core/utils/survey.utils';

/** Datos necesarios para generar el PDF de una respuesta. */
export interface ResponsePdfOptions {
  /** Nombre del formulario; se usa como título del documento. */
  formName: string;
  /** Subtítulo opcional (ej. nombre del beneficiario). */
  subtitle?: string;
  /** Schema SurveyJS del formulario (form.schema). */
  schema: Record<string, unknown>;
  /** Respuestas guardadas (response.answers). */
  answers: Record<string, unknown>;
  /** Nombre del archivo sin extensión; se añade `.pdf`. */
  fileName: string;
}

/**
 * Genera en cliente el PDF de una respuesta de formulario, sin depender de
 * `survey-pdf` (comercial): extrae pregunta → respuesta legible con
 * `survey-core` (MIT, mismo patrón `displayValue` que el Excel de JRS-036)
 * y maqueta el documento directamente con jsPDF (MIT).
 *
 * Tipografía: Roboto (Apache 2.0) embebida desde `public/assets/fonts/`,
 * imprescindible porque las 14 fuentes estándar de PDF no incluyen cirílico
 * y la app tiene locale ucraniano. Se cargan por HTTP la primera vez y se
 * cachean en memoria.
 */
@Injectable({
  providedIn: 'root',
})
export class ResponsePdfExportService {
  private readonly translate = inject(TranslateService);

  /** Geometría A4 en mm. */
  private static readonly PAGE_WIDTH = 210;
  private static readonly PAGE_HEIGHT = 297;
  private static readonly MARGIN = 18;
  /** Reserva inferior para el número de página. */
  private static readonly FOOTER_HEIGHT = 12;

  /** Conversión pt → mm (1 pt = 25.4/72 mm) y factor de interlineado. */
  private static readonly PT_TO_MM = 25.4 / 72;
  private static readonly LINE_SPACING = 1.4;

  /** Cache en memoria de las fuentes en base64 (se descargan una sola vez). */
  private fontCache: { regular: string; bold: string } | null = null;

  /**
   * Nombre de archivo y subtítulo del PDF según las partes del nombre del
   * beneficiario que el usuario haya aceptado incluir en el diálogo de
   * privacidad.
   *
   * Con selección vacía se usa `anonymousId` (el id del beneficiario) en el
   * nombre del archivo Y como subtítulo del documento: permite identificar
   * la respuesta sin exponer el nombre.
   */
  buildPdfNaming(
    formName: string,
    nameParts: (string | undefined)[],
    anonymousId?: string,
  ): { fileName: string; subtitle?: string } {
    const parts = nameParts.filter((p): p is string => !!p);
    const fileParts = parts.length > 0 ? parts : anonymousId ? [anonymousId] : [];
    return {
      fileName: [formName, ...fileParts, 'response'].join('_'),
      subtitle: parts.length > 0 ? parts.join(' ') : anonymousId,
    };
  }

  /**
   * Genera y descarga el PDF de una respuesta.
   * jsPDF se carga con `import()` dinámico → chunk lazy que el navegador
   * solo descarga la primera vez que se pulsa "Download PDF".
   */
  async downloadResponsePdf(options: ResponsePdfOptions): Promise<void> {
    const [{ jsPDF }, fonts] = await Promise.all([import('jspdf'), this.loadFonts()]);

    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    doc.addFileToVFS('Roboto-Regular.ttf', fonts.regular);
    doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
    doc.addFileToVFS('Roboto-Bold.ttf', fonts.bold);
    doc.addFont('Roboto-Bold.ttf', 'Roboto', 'bold');

    await this.renderDocument(doc, options);
    this.renderPageNumbers(doc);

    doc.save(`${options.fileName}.pdf`);
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  private async renderDocument(
    doc: import('jspdf').jsPDF,
    options: ResponsePdfOptions,
  ): Promise<void> {
    const { MARGIN, PAGE_WIDTH } = ResponsePdfExportService;
    const contentWidth = PAGE_WIDTH - MARGIN * 2;
    /** Cursor vertical (baseline de la próxima línea). */
    let y = MARGIN;

    const writeLines = (text: string, fontSize: number, style: 'normal' | 'bold', gray = 0) => {
      doc.setFont('Roboto', style);
      doc.setFontSize(fontSize);
      doc.setTextColor(gray, gray, gray);
      const lineHeight =
        fontSize * ResponsePdfExportService.PT_TO_MM * ResponsePdfExportService.LINE_SPACING;
      const lines: string[] = doc.splitTextToSize(text, contentWidth);
      for (const line of lines) {
        y = this.advanceCursor(doc, y, lineHeight);
        doc.text(line, MARGIN, y);
      }
    };

    // --- Cabecera: título, subtítulo y fecha de generación ---
    writeLines(options.formName, 16, 'bold');
    y += 2;
    if (options.subtitle) {
      writeLines(options.subtitle, 11, 'normal', 90);
    }
    writeLines(formatDate(new Date()), 9, 'normal', 130);
    y += 3;
    doc.setDrawColor(200, 200, 200);
    doc.line(MARGIN, y, PAGE_WIDTH - MARGIN, y);
    y += 2;

    // --- Preguntas y respuestas ---
    // Mismo origen de datos que el Excel de respuestas (JRS-036): el modelo
    // de survey-core resuelve las preguntas y `displayValue` da el texto
    // legible de cada respuesta (choices con su label, matrices, etc.).
    const survey = new SurveyModel(options.schema ?? {});
    // Exports siempre en inglés, independientemente del locale con el que
    // el beneficiario respondió o del locale global activo en el navegador.
    survey.locale = 'en';
    survey.data = options.answers ?? {};
    const questions = survey.getAllQuestions();

    questions.forEach((question, index) => {
      y += 4; // separación entre bloques pregunta+respuesta
      writeLines(`${index + 1}. ${question.title || question.name}`, 11, 'bold');
      y += 1;
      writeLines(this.formatAnswer(question), 11, 'normal', 60);
    });

    // --- Scoring (si el formulario lo tiene) ---
    // Nota: leer el scoring DESPUÉS de las respuestas — doComplete() transiciona
    // el modelo a 'completed' y aquí ya hemos leído todos los displayValue.
    const scoringHtml = this.buildScoringHtml(survey, options.schema);
    if (scoringHtml) {
      // El scoring arranca siempre en página nueva.
      doc.addPage();
      y = MARGIN;
      writeLines(this.translate.instant('forms.response.pdf.scoringTitle'), 13, 'bold');
      y += 2;

      // Con estilo: el HTML se renderiza en el DOM (CSS real del navegador)
      // y se captura como imagen. Si la captura falla, fallback a texto plano.
      const capture = await this.captureScoringCanvas(scoringHtml).catch(() => null);
      if (capture && capture.canvas.height > 0) {
        this.addCanvasSliced(doc, capture.canvas, y, capture.cutPointsPx);
      } else {
        writeLines(this.htmlToPlainText(scoringHtml), 11, 'normal', 60);
      }
    }
  }

  /**
   * HTML de scoring resuelto del formulario, o `null` si no aplica.
   *
   * Misma mecánica que `ui-molecule-dialog-form-scoring`: con las answers ya
   * cargadas, `doComplete()` hace que survey-core recalcule `calculatedValues`
   * y resuelva `completedHtml` / `completedHtmlOnCondition`;
   * `processedCompletedHtml` devuelve ese HTML con los placeholders
   * (`{totalScore}`…) ya sustituidos.
   */
  private buildScoringHtml(survey: SurveyModel, schema: Record<string, unknown>): string | null {
    if (!hasScoring(schema ?? {})) return null;

    // Sin HTML de scoring propio, processedCompletedHtml devolvería el
    // "Thank you" genérico de SurveyJS — no tiene sentido imprimirlo.
    const hasCustomCompletedHtml =
      !!schema['completedHtml'] ||
      (Array.isArray(schema['completedHtmlOnCondition']) &&
        (schema['completedHtmlOnCondition'] as unknown[]).length > 0);
    if (!hasCustomCompletedHtml) return null;

    try {
      survey.doComplete();
      return survey.processedCompletedHtml || null;
    } catch {
      return null;
    }
  }

  /**
   * Renderiza el HTML de scoring en un contenedor fuera de pantalla (con el
   * CSS real del navegador, tipografía de la app incluida) y lo captura a
   * canvas con html2canvas a escala 2x para que imprima nítido.
   *
   * Devuelve además `cutPointsPx`: posiciones verticales (px de canvas) de los
   * bordes superiores de los bloques de primer nivel y de cada fila de tabla,
   * que `addCanvasSliced` usa como puntos de corte seguros al paginar.
   *
   * html2canvas ya viene como optionalDependency de jsPDF (declarada además
   * en package.json); el import dinámico la mantiene en su chunk lazy.
   */
  private async captureScoringCanvas(
    html: string,
  ): Promise<{ canvas: HTMLCanvasElement; cutPointsPx: number[] }> {
    const { PAGE_WIDTH, MARGIN } = ResponsePdfExportService;
    const { default: html2canvas } = await import('html2canvas');
    const scale = 2;

    // Ancho del contenedor = ancho útil A4 convertido a px de CSS (96 dpi),
    // para que el reflow del texto coincida con el que tendrá en el PDF.
    const contentWidthPx = Math.round(((PAGE_WIDTH - MARGIN * 2) / 25.4) * 96);

    const container = document.createElement('div');
    container.id = 'pdf-scoring-capture';
    container.style.position = 'fixed';
    container.style.left = '-10000px';
    container.style.top = '0';
    container.style.width = `${contentWidthPx}px`;
    container.style.backgroundColor = '#ffffff';
    container.innerHTML = html;
    document.body.appendChild(container);

    try {
      // Neutralizar recortes INTERNOS del propio HTML:
      // - Wrappers con overflow hidden/auto (o max-width en tablas) recortan
      //   el contenido por mucho que ensanchemos el contenedor exterior.
      // - Márgenes NEGATIVOS (hack típico de los autores de forms para
      //   compensar el padding de la página completed de SurveyJS en la app)
      //   desplazan el contenido fuera de la caja del contenedor → la captura
      //   sale cortada por la izquierda/arriba. Aquí ese padding no existe,
      //   así que eliminarlos reproduce el diseño previsto.
      container.querySelectorAll<HTMLElement>('*').forEach((el) => {
        el.style.overflow = 'visible';
        if (el.tagName === 'TABLE') {
          el.style.maxWidth = 'none';
        }
        const cs = getComputedStyle(el);
        if (parseFloat(cs.marginLeft) < 0) el.style.marginLeft = '0';
        if (parseFloat(cs.marginRight) < 0) el.style.marginRight = '0';
        if (parseFloat(cs.marginTop) < 0) el.style.marginTop = '0';
        if (parseFloat(cs.marginBottom) < 0) el.style.marginBottom = '0';
      });

      // Tablas (u otros bloques) con anchos fijos pueden desbordar el ancho
      // A4: ensanchar el contenedor a su ancho natural y dejar que el PDF lo
      // encoja proporcionalmente — el contenido sale completo (algo más
      // pequeño) en vez de recortado por la derecha. El ancho real se mide
      // por el borde derecho de CADA descendiente: el rect de una tabla
      // devuelve su tamaño completo aunque su wrapper la recortara, y así
      // detectamos desbordes que no llegan a engordar el scrollWidth del
      // contenedor. Dos pasadas porque el ensanche provoca un nuevo reflow.
      const measureNaturalWidth = (): number => {
        const containerLeft = container.getBoundingClientRect().left;
        let max = Math.max(contentWidthPx, container.scrollWidth);
        container.querySelectorAll('*').forEach((el) => {
          max = Math.max(max, Math.ceil(el.getBoundingClientRect().right - containerLeft));
        });
        return max;
      };
      let naturalWidthPx = measureNaturalWidth();
      container.style.width = `${naturalWidthPx}px`;
      naturalWidthPx = Math.max(naturalWidthPx, measureNaturalWidth());
      container.style.width = `${naturalWidthPx}px`;

      // Puntos de corte seguros para la paginación: no partir filas ni bloques.
      const containerTop = container.getBoundingClientRect().top;
      const cutPointsPx = Array.from(container.querySelectorAll(':scope > *, tr'))
        .map((el) => Math.round((el.getBoundingClientRect().top - containerTop) * scale))
        .filter((top) => top > 0)
        .sort((a, b) => a - b);

      // ⚠️ html2canvas clona el documento en un iframe cuyo viewport, por
      // defecto, mide lo que la ventana actual: contenido más ancho que la
      // ventana se recorta aunque el contenedor esté ensanchado. Hay que
      // declarar el área de captura (`width`/`windowWidth`) al ancho natural
      // y reposicionar el clon a (0,0) para evitar el recorte por coordenadas
      // negativas del posicionamiento fuera de pantalla.
      const canvas = await html2canvas(container, {
        scale,
        backgroundColor: '#ffffff',
        logging: false,
        x: 0,
        y: 0,
        scrollX: 0,
        scrollY: 0,
        width: naturalWidthPx,
        height: container.scrollHeight,
        windowWidth: naturalWidthPx,
        onclone: (clonedDoc) => {
          const clone = clonedDoc.getElementById('pdf-scoring-capture');
          if (clone) {
            clone.style.left = '0';
            clone.style.top = '0';
          }
        },
      });
      return { canvas, cutPointsPx: [...new Set(cutPointsPx)] };
    } finally {
      container.remove();
    }
  }

  /**
   * Inserta un canvas en el documento a ancho completo de contenido,
   * troceándolo en varias páginas si es más alto que el espacio disponible.
   * Cuando hay que cortar, retrocede hasta el último punto de corte seguro
   * (`cutPointsPx`) para no partir una fila o un bloque por la mitad.
   * Recibe y devuelve el cursor vertical (posición del borde superior).
   */
  private addCanvasSliced(
    doc: import('jspdf').jsPDF,
    canvas: HTMLCanvasElement,
    y: number,
    cutPointsPx: number[] = [],
  ): number {
    const { PAGE_WIDTH, PAGE_HEIGHT, MARGIN, FOOTER_HEIGHT } = ResponsePdfExportService;
    const contentWidth = PAGE_WIDTH - MARGIN * 2;
    const maxY = PAGE_HEIGHT - MARGIN - FOOTER_HEIGHT;
    const pxPerMm = canvas.width / contentWidth;

    let offsetPx = 0;
    while (offsetPx < canvas.height) {
      let availableMm = maxY - y;
      // Si queda un hueco ridículo al pie, empezar en página nueva.
      if (availableMm < 15) {
        doc.addPage();
        y = MARGIN;
        availableMm = maxY - y;
      }

      const maxSlicePx = Math.floor(availableMm * pxPerMm);
      let sliceHeightPx = Math.min(canvas.height - offsetPx, maxSlicePx);

      // Si queda contenido debajo (hay que cortar), buscar el último punto de
      // corte seguro dentro del hueco. El umbral del 25% evita rebajar el
      // corte a una lámina minúscula (y de paso garantiza que avanzamos).
      if (offsetPx + sliceHeightPx < canvas.height) {
        const minCutPx = offsetPx + Math.floor(maxSlicePx * 0.25);
        const safeCut = cutPointsPx
          .filter((p) => p > minCutPx && p < offsetPx + sliceHeightPx)
          .pop();
        if (safeCut !== undefined) {
          sliceHeightPx = safeCut - offsetPx;
        }
      }
      const slice = document.createElement('canvas');
      slice.width = canvas.width;
      slice.height = sliceHeightPx;
      const ctx = slice.getContext('2d');
      if (!ctx) break;
      ctx.drawImage(
        canvas,
        0,
        offsetPx,
        canvas.width,
        sliceHeightPx,
        0,
        0,
        canvas.width,
        sliceHeightPx,
      );

      const sliceHeightMm = sliceHeightPx / pxPerMm;
      doc.addImage(slice.toDataURL('image/png'), 'PNG', MARGIN, y, contentWidth, sliceHeightMm);
      y += sliceHeightMm;
      offsetPx += sliceHeightPx;
    }
    return y;
  }

  /**
   * Convierte el HTML de scoring a texto plano conservando los saltos de
   * línea de los elementos de bloque y viñetas en los `<li>`.
   */
  private htmlToPlainText(html: string): string {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    parsed.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
    parsed.querySelectorAll('li').forEach((li) => li.prepend('• '));
    parsed.querySelectorAll('p, div, h1, h2, h3, h4, h5, h6, li, tr').forEach((el) => {
      el.append('\n');
    });
    const text = parsed.body.textContent ?? '';
    // Colapsar saltos múltiples y espacios de indentación del HTML.
    return text
      .split('\n')
      .map((line) => line.trim())
      .join('\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  /**
   * Avanza el cursor una línea, saltando de página si no cabe por encima
   * de la reserva del pie. Devuelve la nueva posición (baseline).
   */
  private advanceCursor(doc: import('jspdf').jsPDF, y: number, lineHeight: number): number {
    const { PAGE_HEIGHT, MARGIN, FOOTER_HEIGHT } = ResponsePdfExportService;
    if (y + lineHeight > PAGE_HEIGHT - MARGIN - FOOTER_HEIGHT) {
      doc.addPage();
      return MARGIN + lineHeight;
    }
    return y + lineHeight;
  }

  /** Pie "n / total" centrado en todas las páginas (segunda pasada). */
  private renderPageNumbers(doc: import('jspdf').jsPDF): void {
    const { PAGE_WIDTH, PAGE_HEIGHT } = ResponsePdfExportService;
    const pageCount = doc.getNumberOfPages();
    doc.setFont('Roboto', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(130, 130, 130);
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.text(`${i} / ${pageCount}`, PAGE_WIDTH / 2, PAGE_HEIGHT - 8, { align: 'center' });
    }
  }

  // ---------------------------------------------------------------------------
  // Datos
  // ---------------------------------------------------------------------------

  /** Texto legible de la respuesta de una pregunta; "—" si está vacía. */
  private formatAnswer(question: { isEmpty?: () => boolean; displayValue?: unknown }): string {
    try {
      if (question.isEmpty?.()) return '—';
      return this.stringifyValue(question.displayValue) || '—';
    } catch {
      return '—';
    }
  }

  /**
   * Normaliza `displayValue` a texto plano: los objetos (ej. matrices) se
   * vuelcan como líneas `fila: valor`; los arrays se unen con comas.
   */
  private stringifyValue(value: unknown): string {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (Array.isArray(value)) {
      return value.map((item) => this.stringifyValue(item)).join(', ');
    }
    if (typeof value === 'object') {
      return Object.entries(value as Record<string, unknown>)
        .map(([key, item]) => `${key}: ${this.stringifyValue(item)}`)
        .join('\n');
    }
    return String(value);
  }

  // ---------------------------------------------------------------------------
  // Fuentes
  // ---------------------------------------------------------------------------

  /** Descarga (una vez) y cachea Roboto Regular/Bold en base64 para jsPDF. */
  private async loadFonts(): Promise<{ regular: string; bold: string }> {
    if (!this.fontCache) {
      const [regular, bold] = await Promise.all([
        this.fetchFontAsBase64('assets/fonts/Roboto-Regular.ttf'),
        this.fetchFontAsBase64('assets/fonts/Roboto-Bold.ttf'),
      ]);
      this.fontCache = { regular, bold };
    }
    return this.fontCache;
  }

  /** Ruta relativa (sin "/" inicial) para respetar el baseHref de GitHub Pages. */
  private async fetchFontAsBase64(path: string): Promise<string> {
    const response = await fetch(path);
    if (!response.ok) {
      throw new Error(`Font fetch failed: ${path} (${response.status})`);
    }
    const buffer = await response.arrayBuffer();
    return this.arrayBufferToBase64(buffer);
  }

  /** btoa por chunks para no desbordar la pila con archivos grandes. */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;
    let binary = '';
    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }
    return btoa(binary);
  }
}
